import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Box,
  Heading,
  Text,
  Badge,
  Skeleton,
  Flex,
  useColorModeValue,
  Divider,
  Code,
  useColorMode,
  Icon,
  Button,
  IconButton,
  useToast,
  Link,
  HStack,
  Input,
  Wrap,
  WrapItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useDisclosure,
} from '@chakra-ui/react'
import { FaDownload, FaFileAlt, FaSync } from 'react-icons/fa'

import { formatDate, isRecordActive } from '../utils/helpers'
import { dataService } from '../services/api'
import { useQuery } from '@tanstack/react-query'
import { useVirtualizer } from '@tanstack/react-virtual'
import EntryDetailModal from './EntryDetailModal'
import type {
  DataEntry,
  DataEntriesWithoutEntriesResponse,
  EntryRecord,
  EntriesV2Response,
  FileRecord,
  User,
} from '../types'

interface DataEntryTableProps {
  recordId: number
  data: {
    record: EntryRecord
    files: FileRecord[]
    user: User
    subscribers?: NonNullable<DataEntriesWithoutEntriesResponse['data']['subscribers']>
  } | null
  isLoading: boolean
  error: unknown
}

const DataEntryTable: React.FC<DataEntryTableProps> = ({
  recordId,
  data,
  isLoading,
  error,
}) => {
  const { colorMode } = useColorMode()
  const tableBg = useColorModeValue('white', 'gray.800')
  const codeBg = useColorModeValue('gray.50', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const headerTextColor = useColorModeValue('gray.600', 'gray.300')
  const rowHoverBg = useColorModeValue('gray.50', 'whiteAlpha.50')

  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(
    null
  )
  const toast = useToast()

  const normalizeEpochMs = (ts: number | null | undefined) => {
    if (ts === null || ts === undefined) return null
    const n = Number(ts)
    if (Number.isNaN(n)) return null
    // Heuristic: < 1e12 is very likely seconds; >= 1e12 is ms.
    return n < 1e12 ? n * 1000 : n
  }

  // Format time with difference from base timestamp
  const formatTimeWithDiff = (
    timestamp: number | null | undefined,
    baseTimestamp: number | null | undefined
  ) => {
    const normalizedTs = normalizeEpochMs(timestamp)
    const normalizedBase = normalizeEpochMs(baseTimestamp)
    
    if (!normalizedTs) return '-'
    
    const formattedTime = formatDate(normalizedTs)
    
    if (!normalizedBase) return formattedTime
    
    const diff = normalizedTs - normalizedBase
    return `${formattedTime} (${diff > 0 ? '+' : ''}${diff}ms)`
  }

  const formatNumber = (num: number) => {
    return Number(num.toFixed(6)).toString()
  }

  const formatDataValues = (dataObj: Record<string, any>) => {
    if (!dataObj || typeof dataObj !== 'object') return dataObj

    return Object.entries(dataObj).reduce((acc, [key, value]) => {
      // If value is a number, format it
      if (typeof value === 'number') {
        acc[key] = formatNumber(value)
      } else {
        // Keep other values as is
        acc[key] = value
      }
      return acc
    }, {} as Record<string, any>)
  }

  // Handle file download
  const handleDownload = async (fileId: number, fileName: string) => {
    try {
      setDownloadingFileId(fileId)
      const response = await dataService.getFileDownloadUrl(fileId)

      if (response.status === 'success' && response.data.download_url) {
        // Create a temporary anchor element to trigger the download
        const link = document.createElement('a')
        link.href = response.data.download_url
        link.setAttribute('download', fileName)
        link.setAttribute('target', '_blank')
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        toast({
          title: 'Download started',
          description: `${fileName} will be downloaded shortly`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      } else {
        throw new Error('Failed to get download URL')
      }
    } catch (error) {
      toast({
        title: 'Download failed',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setDownloadingFileId(null)
    }
  }

  const record = data?.record || null
  const files = data?.files || []
  const user = data?.user || null

  const symbols = record?.symbols ?? []
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const activeSymbol = symbols[activeTabIndex]
  const isActive = record
    ? isRecordActive(record.last_updated, record.update_frequency)
    : false

  // If start time is selected, query backend from that timestamp; otherwise fetch latest data.
  // Using datetime-local keeps the UX simple and avoids extra date picker deps.
  const [startTimeLocal, setStartTimeLocal] = useState<string>('')
  const startTimestampSec = useMemo(() => {
    if (!startTimeLocal) return undefined
    const ms = new Date(startTimeLocal).getTime()
    if (Number.isNaN(ms)) return undefined
    return Math.floor(ms / 1000)
  }, [startTimeLocal])
  const latestData = startTimestampSec === undefined

  // Pagination removed: fetch a single (large) page for the active symbol.
  const pageSize = 1000
  const entriesQuery = useQuery({
    queryKey: ['entries', recordId, activeSymbol, pageSize, latestData, startTimestampSec],
    queryFn: async () => {
      if (!activeSymbol) {
        // should not happen when enabled is true
        return Promise.resolve(null as unknown as EntriesV2Response)
      }
      const resp = await dataService.getDataEntries(recordId, {
        symbol: [activeSymbol],
        size: pageSize,
        latest_data: latestData,
        start_timestamp: latestData ? undefined : startTimestampSec,
      })
      return resp
    },
    enabled: !!recordId && !!activeSymbol,
  })

  const entries: DataEntry[] = useMemo(() => {
    if (!activeSymbol) return []
    if (!entriesQuery.data?.data?.entries) return []
    const direct = entriesQuery.data.data.entries?.[activeSymbol]
    if (direct) return direct
    const firstKey = Object.keys(entriesQuery.data.data.entries ?? {})[0]
    return firstKey ? entriesQuery.data.data.entries[firstKey] ?? [] : []
  }, [entriesQuery.data, activeSymbol])

  const entriesScrollRef = useRef<HTMLDivElement | null>(null)
  // Which symbol the current scroll container belongs to (avoids cross-tab timing issues)
  const scrollContainerSymbolRef = useRef<string | undefined>(undefined)
  const scrollPositionsRef = useRef<Record<string, number>>({})
  const rowHeight = 56
  const virtualCount = activeSymbol ? entries.length : 0

  const rowVirtualizer = useVirtualizer({
    count: virtualCount,
    getScrollElement: () => entriesScrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
  })

  const { isOpen, onOpen, onClose } = useDisclosure()
  const [selectedEntry, setSelectedEntry] = useState<DataEntry | null>(null)

  const openEntryDetail = (entry: DataEntry) => {
    setSelectedEntry(entry)
    onOpen()
  }

  useLayoutEffect(() => {
    if (!activeSymbol) return

    const savedTop = scrollPositionsRef.current[activeSymbol] ?? 0
    let cancelled = false
    let attempts = 0

    const apply = () => {
      if (cancelled) return
      const node = entriesScrollRef.current
      const nodeSymbol = scrollContainerSymbolRef.current
      if (node && nodeSymbol === activeSymbol) {
        node.scrollTop = savedTop
        rowVirtualizer.measure()
        return
      }
      // In lazy/unmount tab switching, the scroll node can be null for a short moment.
      if (attempts < 10) {
        attempts += 1
        requestAnimationFrame(apply)
      }
    }

    requestAnimationFrame(apply)
    return () => {
      cancelled = true
    }
  }, [activeSymbol, rowVirtualizer])

  const handleEntriesScroll = () => {
    const el = entriesScrollRef.current
    if (!el) return
    const symbolForNode = scrollContainerSymbolRef.current
    if (symbolForNode) scrollPositionsRef.current[symbolForNode] = el.scrollTop
  }

  const handleTabChange = useCallback(
    (nextIndex: number) => {
      const el = entriesScrollRef.current
      const symbolForNode = scrollContainerSymbolRef.current
      if (el && symbolForNode) scrollPositionsRef.current[symbolForNode] = el.scrollTop
      setActiveTabIndex(nextIndex)
    },
    []
  )

  if (error) {
    return (
      <Box textAlign="center" py={10} px={6}>
        <Heading as="h2" size="lg" color="red.500">
          Error Loading Data
        </Heading>
        <Text mt={4} color="gray.500">
          {error instanceof Error
            ? error.message
            : 'An unexpected error occurred'}
        </Text>
      </Box>
    )
  }

  return (
    <Box>
      {/* Record Information */}
      <Box mb={6}>
        <Flex justifyContent={'space-between'} alignItems={'center'}>
          <Heading size="lg" mb={2}>
            {record?.type} Record Detail
          </Heading>
        </Flex>

        {/* Record Data Table */}
        <TableContainer
          borderRadius="md"
          boxShadow="sm"
          bg={tableBg}
          overflowX="auto"
          border="1px"
          borderColor={borderColor}
        >
          <Table variant="simple" size="md">
            <Tbody>
              <Tr>
                <Td fontWeight="bold" width="150px">
                  ID
                </Td>
                <Td>{record?.record_id || <Skeleton height="20px" width="50px" />}</Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Record Type</Td>
                <Td>
                  {record ? (
                    <Badge
                      colorScheme={record.type === 'Data' ? 'teal' : 'purple'}
                      fontSize="md"
                      p={1}
                    >
                      {record.type}
                    </Badge>
                  ) : (
                    <Skeleton height="24px" width="80px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" width="150px">
                  Name
                </Td>
                <Td>
                  {record?.name || <Skeleton height="20px" width="50px" />}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" width="150px">
                  Provider Name
                </Td>
                <Td>
                  {user ? (
                    <Text>{user.name || user.id} </Text>
                  ) : (
                    <Skeleton height="20px" width="50px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" width="150px">
                  Provider Contact
                </Td>
                <Td>
                  {user ? (
                    <Text>{user.contact || 'Not Provided'} </Text>
                  ) : (
                    <Skeleton height="20px" width="50px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" verticalAlign="top">
                  Symbols
                </Td>
                <Td>
                  {record ? (
                    record.symbols?.length ? (
                      <Wrap spacing={2}>
                        {record.symbols.map((symbol) => (
                          <WrapItem key={symbol}>
                            <Badge colorScheme="blue" fontSize="xs" px={2} py={1}>
                              {symbol}
                            </Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    ) : (
                      <Text color="gray.500">-</Text>
                    )
                  ) : (
                    <Skeleton height="24px" width="120px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" verticalAlign="top">
                  Target Assets
                </Td>
                <Td>
                  {record ? (
                    record.target_assets?.length ? (
                      <Wrap spacing={2}>
                        {record.target_assets.map((asset) => (
                          <WrapItem key={asset}>
                            <Badge colorScheme="purple" fontSize="xs" px={2} py={1}>
                              {asset}
                            </Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    ) : (
                      <Text color="gray.500">-</Text>
                    )
                  ) : (
                    <Skeleton height="24px" width="120px" />
                  )}
                </Td>
              </Tr>

              <Tr>
                <Td fontWeight="bold">Encryption</Td>
                <Td>
                  {record ? (
                    <Badge
                      colorScheme="gray"
                      fontSize="xs"
                      p={1}
                    >
                      {record.is_encrypted ? 'Encrypted' : 'Unencrypted'}
                    </Badge>
                  ) : (
                    <Skeleton height="24px" width="80px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Update Frequency</Td>
                <Td>
                  {record ? (
                    record?.update_frequency + ' ms'
                  ) : (
                    <Skeleton height="20px" width="80px" />
                  )}
                </Td>
              </Tr>

              <Tr>
                <Td fontWeight="bold">Created At</Td>
                <Td>
                  {record ? (
                    formatDate(record.created_at)
                  ) : (
                    <Skeleton height="20px" width="150px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Last Updated</Td>
                <Td>
                  {record ? (
                    formatDate(record.last_updated)
                  ) : (
                    <Skeleton height="20px" width="150px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Live Start Time</Td>
                <Td>
                  {record ? (
                    record.live_start_time ? (
                      formatDate(record.live_start_time)
                    ) : (
                      'null'
                    )
                  ) : (
                    <Skeleton height="20px" width="80px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Downtime%</Td>
                <Td>
                  {record ? (
                    <HStack spacing={4} flexWrap="wrap">
                      {[
                        { label: '1h', period: '1h' },
                        { label: '6h', period: '6h' },
                        { label: '12h', period: '12h' },
                        { label: '1d', period: '1d' },
                        { label: '7d', period: '7d' },
                        { label: '30d', period: '30d' },
                      ].map(({ label, period }) => (
                        <Text key={period} fontSize="sm">
                          <Text as="span" color="gray.500">{label}:</Text>{' '}
                          <Text as="span" fontWeight="medium">
                            {/* todo: 暂时写死 */}
                            {/* {parseDowntime(record.downtime, period)} */}
                            {
                              isActive ? '<0.01%' : '-'
                            }
                          </Text>
                        </Text>
                      ))}
                    </HStack>
                  ) : (
                    <Skeleton height="20px" width="80px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Fee(USD/Month)</Td>
                <Td>
                  {record ? (
                    record.fee
                  ) : (
                    <Skeleton height="20px" width="80px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" verticalAlign="top">
                  Description
                </Td>
                <Td>
                  {record ? (
                    <Box
                      maxHeight="100px"
                      overflowY="auto"
                      p={2}
                      borderRadius="md"
                      bg={codeBg}
                      css={{
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          width: '10px',
                          background:
                            colorMode === 'light'
                              ? 'rgba(0,0,0,0.05)'
                              : 'rgba(255,255,255,0.05)',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background:
                            colorMode === 'light'
                              ? 'rgba(0,0,0,0.2)'
                              : 'rgba(255,255,255,0.2)',
                          borderRadius: '4px',
                        },
                      }}
                    >
                      <Text fontWeight="medium">{record.description}</Text>
                    </Box>
                  ) : (
                    <Skeleton height="100px" width="100%" />
                  )}
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* Files Table Section */}
      <Divider my={6} />
      <Box mb={6}>
        <Heading size="md" mb={4}>
          Files
        </Heading>

        {isLoading ? (
          <TableContainer
            borderRadius="md"
            boxShadow="sm"
            bg={tableBg}
            overflowX="auto"
            border="1px"
            borderColor={borderColor}
          >
            <Table variant="simple" size="md">
              <Thead>
                <Tr>
                  <Th>ID</Th>
                  <Th>File Name</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {Array.from({ length: 2 }).map((_, index) => (
                  <Tr key={index}>
                    <Td>
                      <Skeleton height="20px" />
                    </Td>
                    <Td>
                      <Skeleton height="20px" />
                    </Td>
                    <Td>
                      <Skeleton height="20px" />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        ) : files.length > 0 ? (
          <TableContainer
            borderRadius="md"
            boxShadow="sm"
            bg={tableBg}
            overflowX="auto"
            border="1px"
            borderColor={borderColor}
          >
            <Table variant="simple" size="md">
              <Thead>
                <Tr>
                  <Th>ID</Th>
                  <Th>File Name</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {files.map((file) => (
                  <Tr key={file.id}>
                    <Td>{file.id}</Td>
                    <Td>
                      <Flex align="center">
                        <Icon as={FaFileAlt} mr={2} />
                        {file.file_name}
                      </Flex>
                    </Td>
                    <Td>
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        leftIcon={<Icon as={FaDownload} />}
                        onClick={() => handleDownload(file.id, file.file_name)}
                        isLoading={downloadingFileId === file.id}
                        loadingText="Downloading"
                      >
                        Download
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        ) : (
          <Flex
            direction="column"
            align="center"
            justify="center"
            p={8}
            bg={tableBg}
            borderRadius="md"
            boxShadow="sm"
            border="1px"
            borderColor={borderColor}
            minH="200px"
          >
            <Icon as={FaFileAlt} fontSize="3xl" color="gray.400" mb={4} />
            <Text fontSize="lg" fontWeight="medium" color="gray.500">
              No files available
            </Text>
            <Text color="gray.400" mt={2} textAlign="center">
              When files are uploaded, they will appear here.
            </Text>
          </Flex>
        )}
      </Box>

      <Divider my={6} />

      {/* Entries Table */}
      <Box>
        <Flex align="center" justify="space-between" gap={3} mb={4} flexWrap="wrap">
          <Heading size="md">Data Entries</Heading>
          <HStack spacing={2} flexWrap="wrap" justify="flex-end">
            <Input
              type="datetime-local"
              size="sm"
              value={startTimeLocal}
              onChange={(e) => setStartTimeLocal(e.target.value)}
              isDisabled={!activeSymbol}
              width="260px"
              title="Select a start time to load data from that timestamp"
            />
            {!!startTimeLocal && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setStartTimeLocal('')}
                isDisabled={!activeSymbol}
              >
                Latest
              </Button>
            )}
            <IconButton
              aria-label="Refresh entries"
              icon={<Icon as={FaSync} />}
              size="sm"
              variant="ghost"
              isDisabled={!activeSymbol}
              isLoading={entriesQuery.isRefetching || entriesQuery.isFetching}
              onClick={() => entriesQuery.refetch()}
            />
          </HStack>
        </Flex>

        {symbols.length > 0 ? (
          <Tabs
            variant="enclosed"
            colorScheme="blue"
            isLazy
            lazyBehavior="unmount"
            index={activeTabIndex}
            onChange={handleTabChange}
          >
            <TabList
              mb="1em"
              overflowX="auto"
              overflowY="hidden"
            >
              {symbols.map((symbol) => (
                <Tab key={symbol} whiteSpace="nowrap">
                  {symbol}
                </Tab>
              ))}
            </TabList>

            <TabPanels>
              {symbols.map((symbol) => {
                const isCurrent = symbol === activeSymbol
                return (
                  <TabPanel key={symbol} px={0}>
                    <Box
                      borderRadius="md"
                      boxShadow="sm"
                      bg={tableBg}
                      border="1px"
                      borderColor={borderColor}
                      overflow="hidden"
                    >
                      {/* header */}
                      <Flex
                        px={4}
                        py={3}
                        borderBottom="1px"
                        borderColor={borderColor}
                        fontSize="sm"
                        fontWeight="semibold"
                        color={headerTextColor}
                      >
                        <Box flex="0 0 110px">ID</Box>
                        <Box flex="0 0 110px">Batch ID</Box>
                        <Box flex="0 0 210px">Time</Box>
                        <Box flex="0 0 270px">Runner Time</Box>
                        <Box flex="0 0 270px">Server Time</Box>
                        <Box flex="0 0 140px">Symbol</Box>
                        <Box flex="1 1 auto">{record?.is_encrypted ? 'Commitment' : 'Data'}</Box>
                        {record?.is_encrypted && <Box flex="0 0 260px">Txn Hash</Box>}
                      </Flex>

                      {isLoading || (isCurrent && entriesQuery.isLoading) ? (
                        <Box p={4}>
                          {Array.from({ length: 13 }).map((_, i) => (
                            <Skeleton key={i} height={`${rowHeight}px`} mb={2} />
                          ))}
                        </Box>
                      ) : isCurrent ? (
                        <Box
                          ref={(node) => {
                            entriesScrollRef.current = node
                            scrollContainerSymbolRef.current = node ? symbol : undefined
                          }}
                          maxH="60vh"
                          overflowY="auto"
                          onScroll={handleEntriesScroll}
                        >
                          {entries.length === 0 ? (
                            <Box p={8} textAlign="center">
                              <Text color="gray.500">No data entries found for {symbol}</Text>
                            </Box>
                          ) : (
                            <Box
                              height={`${rowVirtualizer.getTotalSize()}px`}
                              position="relative"
                            >
                              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                const idx = virtualRow.index
                                const entry = entries[idx]

                                return (
                                  <Flex
                                    key={virtualRow.key}
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    transform={`translateY(${virtualRow.start}px)`}
                                    height={`${virtualRow.size}px`}
                                    px={4}
                                    align="center"
                                    borderBottom="1px"
                                    borderColor={borderColor}
                                    _hover={{
                                      bg: rowHoverBg,
                                    }}
                                  >
                                    <>
                                      <Box flex="0 0 110px">{entry?.id}</Box>
                                      <Box flex="0 0 110px">
                                        {entry?.batch_id !== undefined && entry?.batch_id !== null
                                          ? entry.batch_id
                                          : '-'}
                                      </Box>
                                      <Box flex="0 0 210px">
                                        {entry
                                          ? formatDate(normalizeEpochMs(entry.timestamp))
                                          : '-'}
                                      </Box>
                                      <Box flex="0 0 270px">
                                        {entry
                                          ? formatTimeWithDiff(entry.runner_timestamp, entry.timestamp)
                                          : '-'}
                                      </Box>
                                      <Box flex="0 0 270px">
                                        {entry
                                          ? formatTimeWithDiff(entry.server_timestamp, entry.timestamp)
                                          : '-'}
                                      </Box>
                                      <Box flex="0 0 140px">{entry?.symbol || symbol}</Box>
                                      <Flex flex="1 1 auto" minW={0} align="center" gap={3}>
                                        <Code
                                          fontSize="xs"
                                          bg={codeBg}
                                          px={2}
                                          py={1}
                                          borderRadius="md"
                                          noOfLines={1}
                                          maxW="100%"
                                        >
                                          {record?.is_encrypted
                                            ? entry?.commitment || 'No commitment'
                                            : entry
                                              ? JSON.stringify(formatDataValues(entry.data), null, 2)
                                              : '-'}
                                        </Code>
                                        {entry && (
                                          <Button
                                            size="xs"
                                            variant="ghost"
                                            onClick={() => openEntryDetail(entry)}
                                          >
                                            View
                                          </Button>
                                        )}
                                      </Flex>
                                      {record?.is_encrypted && (
                                        <Box flex="0 0 260px" minW={0}>
                                          {entry?.txn_hash ? (
                                            <Link
                                              href={`https://sepolia.basescan.org/tx/${entry.txn_hash}`}
                                              isExternal
                                              color="blue.500"
                                              _hover={{ textDecoration: 'underline' }}
                                              noOfLines={1}
                                            >
                                              {entry.txn_hash}
                                            </Link>
                                          ) : (
                                            'null'
                                          )}
                                        </Box>
                                      )}
                                    </>
                                  </Flex>
                                )
                              })}
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Box p={8} textAlign="center">
                          <Text color="gray.500">Switch to this tab to load entries</Text>
                        </Box>
                      )}
                    </Box>
                  </TabPanel>
                )
              })}
            </TabPanels>
          </Tabs>
        ) : (
           <TableContainer
             borderRadius="md"
             boxShadow="sm"
             bg={tableBg}
             overflowX="auto"
             border="1px"
             borderColor={borderColor}
           >
             <Table variant="simple" size="md">
               <Thead>
                 <Tr>
                   <Th>ID</Th>
                   <Th>Batch ID</Th>
                   <Th>Time</Th>
                   <Th>Runner Time</Th>
                   <Th>Server Time</Th>
                   <Th>Symbol</Th>
                   <Th>{record?.is_encrypted ? 'Commitment' : 'Data'}</Th>
                   {record?.is_encrypted && <Th>Txn Hash</Th>}
                 </Tr>
               </Thead>
               <Tbody>
                 <Tr>
                    <Td colSpan={record?.is_encrypted ? 8 : 7} textAlign="center" py={6}>
                      <Text color="gray.500">No data entries found</Text>
                    </Td>
                 </Tr>
               </Tbody>
             </Table>
           </TableContainer>
        )}
      </Box>

      <EntryDetailModal
        isOpen={isOpen}
        onClose={onClose}
        selectedEntry={selectedEntry}
        record={record}
        codeBg={codeBg}
        formatDataValues={formatDataValues}
        maxBodyHeight="70vh"
      />
    </Box>
  )
}

export default DataEntryTable
